import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid } from '@material-ui/core';
import { Typography, SimpleLoader, Alert } from '@dev/ee-components';
import { useDispatch, useSelector } from 'react-redux';
import { ErrorSubscriberOrder } from '@/components/CampaignCreator/CampaignCreatorInterface';
import { isCampaignTemplateSet } from '@/components/CampaignCreator/helpers';
import { getCampainCreatorStore, SetCampaignErrorsSubscriber } from '@/store/campaignCreator/actions';
import { CampaignErrorNames } from '@/store/campaignCreator/types';
import { INDEX_PLACEHOLDER } from '@/services/constants';
import { HistoryBackActionContext, useHistoryBackAction } from '@/services/hooks/useHistoryBackAction';
import {
    CampaignFrom,
    CampaignReplyTo,
    CampaignSubject,
    CampaignPreviewText,
    ChooseContentSection,
    TemplatePreview,
    CampaignRawEditor,
    PreviewOverlay,
} from './components';
import { useGetCampaignInfo } from '../hooks/useGetCampaignInfo';
import { SubjectAndContentProps } from './SubjectAndContentInterface';
import { Divider } from './components/styledComponents';
import { EditorDragAndDropModal } from './components/EditorDragAndDropModal';
import { TemplateEditorModal } from './components/TemplateEditorModal';

export const SubjectAndContent = ({ index }: SubjectAndContentProps): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { campaign, errors, editorDragAndDrop } = useSelector(getCampainCreatorStore);
    const { fromEmail, replyEmail } = useGetCampaignInfo();
    const [showReplyTo, setShowReplyTo] = useState(fromEmail !== replyEmail);

    const campaignTemplate = campaign?.CampaignTemplates?.[index];
    const subjectId = `campaign-creator-subject-input-${index}`;
    const subjecErrorName = CampaignErrorNames.CAMPAIGN_TEMPLATES_BODY_SUBJECT.replace(INDEX_PLACEHOLDER, `${index}`);
    const isTemplateSelected = isCampaignTemplateSet(campaignTemplate);
    const isTemplateLoading = false;
    // const isTemplateLoading = !!templatesLoading[index];

    const contentErrorId = `campaign-creator-content-${index}`;
    const contentErrorName = CampaignErrorNames.TEMPLATE_SIZE.replace(INDEX_PLACEHOLDER, `${index}`);
    const contentHasError = !!errors?.[contentErrorName];

    const noEmailTemplateErrorName = CampaignErrorNames.NO_EMAIL_TEMPLATE.replace(INDEX_PLACEHOLDER, `${index}`);
    const emailTemplateHasError = !!errors?.[noEmailTemplateErrorName];

    const isSavedRef = React.useRef(true);
 
    const historyBackAction = useHistoryBackAction({
        isSavedRef
    });
    
    useEffect((): void => {
        dispatch(
            SetCampaignErrorsSubscriber({
                id: contentErrorId,
                errorName: contentErrorName,
                order: ErrorSubscriberOrder.TEMPLATE_SUBJECT,
            })
        );
        dispatch(
            SetCampaignErrorsSubscriber({
                id: contentErrorId,
                errorName: noEmailTemplateErrorName,
                order: ErrorSubscriberOrder.TEMPLATE_SUBJECT,
            })
        );
    }, [index]);

    useEffect((): void => {
        dispatch(
            SetCampaignErrorsSubscriber({
                id: subjectId,
                errorName: subjecErrorName,
                order: ErrorSubscriberOrder.TEMPLATE_SUBJECT,
            })
        );
    }, [index]);
    
    return (
        <Grid container>
            <HistoryBackActionContext.Provider value={historyBackAction}>
                <CampaignFrom showReplyTo={showReplyTo} setShowReplyTo={setShowReplyTo} />
                {showReplyTo && <CampaignReplyTo />}
                <CampaignSubject id={subjectId} />
                <CampaignPreviewText />
                <Divider />
                <Grid item xs={12} style={{ marginTop: 32 }} id={contentErrorId}>
                    <Typography variant="h2">{t('content')}</Typography>
                </Grid>
                {contentHasError && (
                    <Grid item xs={12} style={{ marginTop: 16 }}>
                        <Alert severity="error" body={t('templatesizeerror')} />
                    </Grid>
                )}
                {emailTemplateHasError && (
                    <Grid item xs={12} style={{ marginTop: 16 }}>
                        <Alert severity="error" body={t('noemailtemplateerror')} />
                    </Grid>
                )}
                {!isTemplateSelected && !isTemplateLoading && <ChooseContentSection openInOverlay />}
                {isTemplateLoading && (
                    <Grid container xs={12} justifyContent="center" alignItems="center" style={{ height: 300 }}>
                        <SimpleLoader />
                    </Grid>
                )}
                {isTemplateSelected && <TemplatePreview />}
                <CampaignRawEditor />
                {editorDragAndDrop.isOpen && <EditorDragAndDropModal />}
                <TemplateEditorModal />
                <PreviewOverlay />
            </HistoryBackActionContext.Provider>
        </Grid>
    );
};
